import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, RefreshControl, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Scroll, Stack } from '@/components/layout';
import { STRINGS } from '@/constants/strings';
import { useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';

import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import HeaderSummary from '@/features/guardian-player/components/player-detail/header-summary';
import LessonsSection from '@/features/guardian-player/components/player-detail/lessons-section';
import QuickLinksGrid from '@/features/guardian-player/components/player-detail/quick-links-grid';
import StatsRow from '@/features/guardian-player/components/player-detail/stats-row';
import { usePlayerDetail } from '@/features/guardian-player/hooks/use-player-detail';

import MobileHeader from './mobile-header';
import { useStyles } from './styles';

import type { PlayerDetailContentProps } from '@/features/guardian-player/components/player-detail/types';

const strings = STRINGS.guardianPlayer.playerDetail;

export default function PlayerDetailContent({
  player,
  onBack,
  onOpenProgress,
  onOpenNotes,
  onOpenGuardians,
  onOpenComingSoon,
  onBookClass,
  onEdit,
}: PlayerDetailContentProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const {
    player: mergedPlayer,
    firstName,
    fullName,
    age,
    progressLabel,
    notesCount,
    stats,
    refetch,
    handleFeatureUnderProgress,
  } = usePlayerDetail(player);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  // Fresh element per Scroll — reusing one RefreshControl node across panes remounts incorrectly.
  const createRefreshControl = useCallback(
    () =>
      Platform.OS === 'web' ? undefined : (
        <RefreshControl
          refreshing={isRefreshing}
          onRefresh={handleRefresh}
          tintColor={guardianColors['Main/Main']}
          colors={[guardianColors['Main/Main']]}
          progressViewOffset={insets.top}
        />
      ),
    [handleRefresh, insets.top, isRefreshing],
  );

  const notesLabel = strings.cards.notesCount(notesCount);
  const screenTitle = strings.screenTitle.replace('{name}', firstName);

  const headerPlayer = useMemo(
    () => ({
      name: fullName,
      age,
      level: mergedPlayer.level,
      avatarUrl: mergedPlayer.avatarUrl,
    }),
    [fullName, age, mergedPlayer.level, mergedPlayer.avatarUrl],
  );

  const bottomPaddingStyle = useMemo(
    () => ({ paddingBottom: insets.bottom + scale(24) }),
    [insets.bottom, scale],
  );

  const quickLinksGrid = (
    <QuickLinksGrid
      progressLabel={progressLabel ?? '-'}
      notesLabel={notesLabel}
      guardiansLabel={stats.numberGuardian}
      packageLabel={stats.packageLeft}
      onPressProgress={onOpenProgress}
      onPressNotes={onOpenNotes}
      onPressGuardians={onOpenGuardians}
      onPressPackage={onOpenComingSoon}
    />
  );

  const lessonsSection = (
    <LessonsSection
      playerFirstName={firstName}
      lessons={[]}
      onPressViewDetail={handleFeatureUnderProgress}
      onPressReschedule={handleFeatureUnderProgress}
      onPressLocation={handleFeatureUnderProgress}
      onPressBookClass={onBookClass}
    />
  );

  if (isTablet) {
    return (
      <Stack style={styles.tabletRoot}>
        <GuardianScreenHeader
          title={screenTitle}
          onBack={onBack}
          rightIcon="IcEditAvatar"
          onPressRight={onEdit}
          rightAccessibilityLabel="Edit player"
        />
        <Stack flex={1} style={styles.tabletBody}>
          <Scroll
            style={styles.tabletLeftPane}
            alwaysBounceVertical
            contentContainerStyle={[styles.tabletLeftPaneContent, bottomPaddingStyle]}
            refreshControl={createRefreshControl()}>
            <HeaderSummary player={headerPlayer} />
            <StatsRow stats={stats} />
            {quickLinksGrid}
          </Scroll>
          <Scroll
            style={styles.tabletRightPane}
            alwaysBounceVertical
            contentContainerStyle={[styles.tabletRightPaneContent, bottomPaddingStyle]}
            refreshControl={createRefreshControl()}>
            {lessonsSection}
          </Scroll>
        </Stack>
      </Stack>
    );
  }

  return (
    <LinearGradient
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.91]}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.mobileRoot}>
      <MobileHeader onBack={onBack} onEdit={onEdit} />
      <Scroll
        style={styles.mobileScroll}
        showsVerticalScrollIndicator={false}
        alwaysBounceVertical
        contentContainerStyle={[
          styles.scrollContent,
          { paddingTop: insets.top + scale(4) },
          bottomPaddingStyle,
        ]}
        refreshControl={createRefreshControl()}>
        {isRefreshing ? (
          <View style={styles.refreshIndicator}>
            <ActivityIndicator size="small" color={guardianColors['Main/Main']} />
          </View>
        ) : null}
        <HeaderSummary player={headerPlayer} />
        <StatsRow stats={stats} />
        {quickLinksGrid}
        {lessonsSection}
      </Scroll>
    </LinearGradient>
  );
}
